import { createContext, useContext, type HTMLAttributes, type ReactNode, type Ref } from 'react'
import { Card, CardContent } from '@bnsn/ui/components/card'
import { cn } from '@bnsn/ui/lib'
import { StrategistChatBnsnAvatar } from './bnsn-avatar'
import { StrategistChatUserAvatar } from './user-avatar'

/**
 * The role of a message sender in the strategist chat.
 */
export type StrategistChatMessageRole = 'user' | 'assistant'

/**
 * Status information for a chat message, indicating the current state of processing.
 *
 * @property state - The current state of the message processing
 * @property message - A human-readable message describing the status
 */
export interface StrategistChatMessageStatus {
  state: 'loading' | 'success' | 'error' | 'aborted'
  message: string
}

/**
 * Context value shared between StrategistChatMessage and its child components.
 *
 * @property role - The role of the message sender ('user' or 'assistant')
 * @property status - Optional status information for the message
 */
export interface StrategistChatMessageContextValue {
  role: StrategistChatMessageRole
  status?: StrategistChatMessageStatus
}

const StrategistChatMessageContext = createContext<StrategistChatMessageContextValue | undefined>(undefined)

/**
 * Hook to access the StrategistChatMessage context.
 *
 * Must be used within a StrategistChatMessage component.
 *
 * @returns The current message context containing role and optional status
 * @throws Error if used outside of a StrategistChatMessage component
 *
 * @example
 * ```tsx
 * const CustomComponent = () => {
 *   const { role, status } = useStrategistChatMessageContext()
 *   return <div>Role: {role}</div>
 * }
 * ```
 *
 * @see {@link file://./../../__tests__/message.test.tsx} - Unit tests
 * @see {@link file://./../../../../../apps/docs/src/ui/organisms/strategist-chat/message.stories.tsx} - Storybook stories
 */
export const useStrategistChatMessageContext = (): StrategistChatMessageContextValue => {
  const context = useContext(StrategistChatMessageContext)
  if (!context) {
    throw new Error('StrategistChatMessage components must be used within a StrategistChatMessage component')
  }
  return context
}

/**
 * Props for the StrategistChatMessage component.
 *
 * @extends HTMLAttributes<HTMLDivElement>
 */
export interface StrategistChatMessageProps extends HTMLAttributes<HTMLDivElement> {
  /** The role of the message sender */
  role: StrategistChatMessageRole
  /** React ref for the root element */
  ref?: Ref<HTMLDivElement>
  /** Optional status information for the message */
  status?: StrategistChatMessageStatus
}

/**
 * Root message container component for the Strategist Chat.
 *
 * Provides context for child components to access the message role and status.
 * User messages are right-aligned, assistant messages are left-aligned.
 *
 * This is a composable component - use with StrategistChatMessageAvatar,
 * StrategistChatMessageContent, and StrategistChatMessageStatus for full functionality.
 *
 * @param props - Component props
 * @returns The rendered message container
 *
 * @example Basic usage
 * ```tsx
 * <StrategistChatMessage role="user">
 *   <StrategistChatMessageAvatar />
 *   <StrategistChatMessageContent>Hello world</StrategistChatMessageContent>
 * </StrategistChatMessage>
 * ```
 *
 * @example With status
 * ```tsx
 * <StrategistChatMessage role="assistant" status={{ state: 'loading', message: 'Thinking...' }}>
 *   <StrategistChatMessageStatus />
 *   <StrategistChatMessageAvatar />
 *   <StrategistChatMessageContent>Processing your request...</StrategistChatMessageContent>
 * </StrategistChatMessage>
 * ```
 *
 * @see {@link file://./../../__tests__/message.test.tsx} - Unit tests
 * @see {@link file://./../../../../../apps/docs/src/ui/organisms/strategist-chat/message.stories.tsx} - Storybook stories
 */
export const StrategistChatMessage = ({
  className,
  role,
  status,
  children,
  ref,
  ...props
}: StrategistChatMessageProps): ReactNode => {
  const isUser = role === 'user'

  return (
    <StrategistChatMessageContext.Provider value={{ role, status }}>
      <div
        ref={ref}
        className={cn('flex items-center gap-2', isUser && 'justify-end', className)}
        data-role={role}
        data-testid={`message-${role}`}
        {...props}
      >
        {children}
      </div>
    </StrategistChatMessageContext.Provider>
  )
}
StrategistChatMessage.displayName = 'StrategistChatMessage'

/**
 * Props for the StrategistChatMessageAvatar component.
 *
 * @extends HTMLAttributes<HTMLDivElement>
 */
export interface StrategistChatMessageAvatarProps extends HTMLAttributes<HTMLDivElement> {
  /** React ref for the root element */
  ref?: Ref<HTMLDivElement>
}

/**
 * Avatar component that renders the appropriate avatar based on message role.
 *
 * Automatically renders BNSN avatar for assistant messages and user avatar for user messages.
 * Custom children can be provided to override the default avatar.
 *
 * Must be used within a StrategistChatMessage component.
 *
 * @param props - Component props
 * @returns The rendered avatar
 *
 * @example Default avatar (auto-selected based on role)
 * ```tsx
 * <StrategistChatMessage role="assistant">
 *   <StrategistChatMessageAvatar />
 * </StrategistChatMessage>
 * ```
 *
 * @example Custom avatar
 * ```tsx
 * <StrategistChatMessage role="user">
 *   <StrategistChatMessageAvatar>
 *     <CustomAvatarComponent />
 *   </StrategistChatMessageAvatar>
 * </StrategistChatMessage>
 * ```
 *
 * @example Positioned for user messages (avatar on right)
 * ```tsx
 * <StrategistChatMessage role="user">
 *   <StrategistChatMessageAvatar className="order-last" />
 *   <StrategistChatMessageContent>Message text</StrategistChatMessageContent>
 * </StrategistChatMessage>
 * ```
 *
 * @see {@link file://./../../__tests__/message.test.tsx} - Unit tests
 * @see {@link file://./../../../../../apps/docs/src/ui/organisms/strategist-chat/message.stories.tsx} - Storybook stories
 */
export const StrategistChatMessageAvatar = ({
  className,
  children,
  ref,
  ...props
}: StrategistChatMessageAvatarProps): ReactNode => {
  const { role } = useStrategistChatMessageContext()

  return (
    <div ref={ref} className={cn('flex-shrink-0', className)} data-testid={`avatar-${role}`} {...props}>
      {children ?? (role === 'assistant' ? <StrategistChatBnsnAvatar /> : <StrategistChatUserAvatar />)}
    </div>
  )
}
StrategistChatMessageAvatar.displayName = 'StrategistChatMessageAvatar'

/**
 * Props for the StrategistChatMessageContent component.
 *
 * @extends HTMLAttributes<HTMLDivElement>
 */
export interface StrategistChatMessageContentProps extends HTMLAttributes<HTMLDivElement> {
  /** React ref for the root element */
  ref?: Ref<HTMLDivElement>
}

/**
 * Content container for chat messages.
 *
 * A Card wrapper with styling based on the message role:
 * - User messages: muted background with padding
 * - Assistant messages: white background
 *
 * Must be used within a StrategistChatMessage component.
 *
 * @param props - Component props
 * @returns The rendered content container
 *
 * @example
 * ```tsx
 * <StrategistChatMessage role="user">
 *   <StrategistChatMessageContent>
 *     This is the message content
 *   </StrategistChatMessageContent>
 * </StrategistChatMessage>
 * ```
 *
 * @see {@link file://./../../__tests__/message.test.tsx} - Unit tests
 * @see {@link file://./../../../../../apps/docs/src/ui/organisms/strategist-chat/message.stories.tsx} - Storybook stories
 */
export const StrategistChatMessageContent = ({
  className,
  children,
  ref,
  ...props
}: StrategistChatMessageContentProps): ReactNode => {
  const { role } = useStrategistChatMessageContext()
  const isUser = role === 'user'

  return (
    <Card
      ref={ref}
      className={cn('border', isUser ? 'bg-muted p-3' : 'bg-white', className)}
      data-testid={`card-${role}`}
      {...props}
    >
      <CardContent className={isUser ? 'p-0' : 'p-3'}>{children}</CardContent>
    </Card>
  )
}
StrategistChatMessageContent.displayName = 'StrategistChatMessageContent'

/**
 * Props for the StrategistChatMessageStatus component.
 *
 * @extends HTMLAttributes<HTMLDivElement>
 */
export interface StrategistChatMessageStatusProps extends HTMLAttributes<HTMLDivElement> {
  /** React ref for the root element */
  ref?: Ref<HTMLDivElement>
}

/**
 * Status badge component that displays the current processing state of a message.
 *
 * Renders a colored badge based on the status state:
 * - loading: secondary background
 * - success: accent background
 * - error: muted background
 * - aborted: danger background
 *
 * Returns null if no status is provided in the context.
 * Must be used within a StrategistChatMessage component.
 *
 * @param props - Component props
 * @returns The rendered status badge, or null if no status
 *
 * @example
 * ```tsx
 * <StrategistChatMessage role="assistant" status={{ state: 'loading', message: 'Processing...' }}>
 *   <StrategistChatMessageStatus />
 *   <StrategistChatMessageContent>Working on it...</StrategistChatMessageContent>
 * </StrategistChatMessage>
 * ```
 *
 * @see {@link file://./../../__tests__/message.test.tsx} - Unit tests
 * @see {@link file://./../../../../../apps/docs/src/ui/organisms/strategist-chat/message.stories.tsx} - Storybook stories
 */
export const StrategistChatMessageStatus = ({ className, ref, ...props }: StrategistChatMessageStatusProps): ReactNode => {
  const { status } = useStrategistChatMessageContext()

  if (!status) {
    return null
  }

  return (
    <div ref={ref} className={cn('flex items-center gap-2', className)} data-testid="message-status" {...props}>
      <span
        className={cn(
          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold uppercase',
          status.state === 'loading'
            ? 'bg-secondary'
            : status.state === 'success'
              ? 'bg-accent text-accent-800'
              : status.state === 'error'
                ? 'bg-muted'
                : 'bg-danger-200',
        )}
        data-testid={`status-badge-${status.state}`}
      >
        {status.state.charAt(0).toUpperCase() + status.state.slice(1)}
      </span>
      {status.message && (
        <span className="text-sm text-muted-foreground" data-testid="status-message">
          {status.message}
        </span>
      )}
    </div>
  )
}
StrategistChatMessageStatus.displayName = 'StrategistChatMessageStatus'
